import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, RefreshCw, ChevronLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const PRIORITY_STYLE = {
  3: { label: "CRITICAL", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.4)", dot: "#EF4444" },
  2: { label: "WARNING", bg: "rgba(46,130,246,0.10)", border: "rgba(46,130,246,0.35)", dot: "#2E82F6" },
  1: { label: "MINOR", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.3)", dot: "#94A3B8" },
};

export default function AdminIncidents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [filter, setFilter] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/incident-logs${filter ? `?priority=${filter}` : ""}`);
      setData(data);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  };
  // reload when the priority filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="min-h-screen" style={{ background: "var(--ek-page)", color: "var(--ek-text)" }}>
      <div className="max-w-4xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-6 w-6" style={{ color: "var(--ek-primary)" }} />
            <div>
              <h1 className="font-head font-bold text-2xl tracking-tight" data-testid="admin-incidents-title">System Incident Ledger</h1>
              <p className="ek-mono-label mt-1">IMMUTABLE · TAMPER-EVIDENT</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="outline" data-testid="admin-refresh-btn" className="rounded-md" style={{ borderColor: "var(--ek-border)" }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Link to="/" className="text-sm inline-flex items-center gap-1 underline" style={{ color: "var(--ek-primary)" }}>
              <ChevronLeft className="h-4 w-4" /> App
            </Link>
          </div>
        </div>

        {forbidden ? (
          <div className="border rounded-lg p-8 text-center" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }} data-testid="admin-forbidden">
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>Administrator access required.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              {[{ v: 0, t: "All" }, { v: 3, t: "Critical" }, { v: 2, t: "Warnings" }].map((f) => (
                <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`admin-filter-${f.v}`}
                  className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                  style={{ borderColor: filter === f.v ? "var(--ek-primary)" : "var(--ek-border)", color: filter === f.v ? "var(--ek-primary)" : "var(--ek-text-secondary)", background: filter === f.v ? "rgba(46,130,246,0.1)" : "transparent" }}>
                  {f.t}
                </button>
              ))}
              {data?.counts && (
                <span className="ml-auto ek-mono-label" data-testid="admin-counts">L3: {data.counts.l3} · L2: {data.counts.l2}</span>
              )}
            </div>

            {loading ? (
              <div className="text-sm py-10 text-center" style={{ color: "var(--ek-text-muted)" }}>Loading ledger…</div>
            ) : !data?.logs?.length ? (
              <div className="border rounded-lg p-10 text-center" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }} data-testid="admin-empty">
                <CheckCircle2 className="h-6 w-6 mx-auto mb-2" style={{ color: "var(--ek-secure)" }} />
                <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>No incidents logged. All systems nominal.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-incident-list">
                {data.logs.map((r) => {
                  const ps = PRIORITY_STYLE[r.priority] || PRIORITY_STYLE[1];
                  const open = expanded === r.incident_id;
                  return (
                    <div key={r.incident_id} className="border rounded-lg overflow-hidden" style={{ borderColor: ps.border, background: ps.bg }} data-testid={`incident-${r.incident_id}`}>
                      <button onClick={() => setExpanded(open ? null : r.incident_id)} className="w-full text-left p-4">
                        <div className="flex items-center gap-3">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: ps.dot }} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-semibold" style={{ color: "var(--ek-text)" }}>{r.incident_id}</span>
                              <span className="ek-mono-label" style={{ color: ps.dot }}>{ps.label}</span>
                              {r.hash_valid ? (
                                <span className="ek-mono-label inline-flex items-center gap-1" style={{ color: "var(--ek-secure)" }}><CheckCircle2 className="h-3 w-3" /> HASH OK</span>
                              ) : (
                                <span className="ek-mono-label inline-flex items-center gap-1" style={{ color: "#EF4444" }}><AlertTriangle className="h-3 w-3" /> TAMPERED</span>
                              )}
                            </div>
                            <div className="text-sm mt-1 truncate" style={{ color: "var(--ek-text-secondary)" }}>{r.subsystem} — {r.metric_code}</div>
                            <div className="ek-mono-label mt-1">{r.timestamp} UTC</div>
                          </div>
                        </div>
                      </button>
                      {open && (
                        <div className="px-4 pb-4">
                          <pre className="text-xs rounded-md p-3 overflow-x-auto" style={{ background: "rgba(0,0,0,0.35)", color: "#9fb2cf", whiteSpace: "pre" }} data-testid={`incident-telemetry-${r.incident_id}`}>{JSON.stringify(r.telemetry, null, 2)}</pre>
                          <div className="ek-mono-label mt-2" style={{ color: "var(--ek-text-muted)" }}>STATUS: {r.system_status || "—"}</div>
                          <div className="ek-mono-label mt-1" style={{ color: "var(--ek-text-muted)", wordBreak: "break-all" }}>HASH: {r.entry_hash}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
